import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Header } from '@deere/fuel-react/Header';
import { HeaderActionArea } from '@deere/fuel-react/HeaderActionArea';
import { Nav } from '@deere/fuel-react/Nav';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { useUserLogin } from '../shared/UserLogin';
import { ThemeModeSelect } from './ThemeModeSelect';
import { useLoginState } from '../shared/LoginStateContext';
import { getContactUsLink } from '../shared/UtilFunctions';
import { getEnvVar } from '../utils/getEnvVar';
import {
  ALL_OTHER_JDSN_APPLICATIONS,
  MATERIAL_GROUP_CODE,
  MGC,
  SBF,
  SEP,
  SMALL_BUSINESS_FORM,
  SOURCING_EXECUTION_PLAN,
  SUPPLIER_PROFILE,
  TCS,
  TOTAL_COST_SOLUTION
} from '../shared/AppConstants';

function openLink(event, url) {
  event.preventDefault();

  window.open(url);
}

function AppNavigation({ hasCreateAccess, isAdmin, roles }) {
  const canShowRestrictedNav = roles && roles.length > 0;
  const location = useLocation();
  const items = [
    {
      component: NavLink,
      to: '/',
      end: true,
      label: 'Search',
      selected: location.pathname === '/',
      startIcon: <fuel-icon name="home" />
    },
    ...(canShowRestrictedNav && hasCreateAccess
      ? [
          {
            component: NavLink,
            to: '/createStrategy',
            label: 'Create Strategy',
            selected: location.pathname.startsWith('/createStrategy'),
            startIcon: <fuel-icon name="add" />
          },
          {
            href: getEnvVar('VITE_STRATEGY_REPORTS_POWER_BI_REPORT'),
            label: 'Part Level Spend',
            startIcon: <fuel-icon name="bar_chart" />,
            target: '_blank'
          }
        ]
      : []),
    {
      component: NavLink,
      to: '/supplierSpendShift',
      label: 'Supplier Spend Shift',
      selected: location.pathname === '/supplierSpendShift',
      startIcon: <fuel-icon name="bar_chart" />
    },
    {
      label: 'Applications',
      startIcon: <fuel-icon name="apps" />,
      subNav: [
        {
          href: getEnvVar('VITE_SEP_APP_BASE_URL'),
          label: `${SOURCING_EXECUTION_PLAN} (${SEP})`,
          target: '_blank'
        },
        {
          href: getEnvVar('VITE_MGC_APP_BASE_URL'),
          label: `${MATERIAL_GROUP_CODE} (${MGC})`,
          target: '_blank'
        },
        {
          href: getEnvVar('VITE_TCS_APP_BASE_URL'),
          label: `${TOTAL_COST_SOLUTION} (${TCS})`,
          target: '_blank'
        },
        {
          href: getEnvVar('VITE_SUP_PROFILE_APP_BASE_URL'),
          label: SUPPLIER_PROFILE,
          target: '_blank'
        },
        {
          href: getEnvVar('VITE_SDF_APP_BASE_URL'),
          label: `${SMALL_BUSINESS_FORM} (${SBF})`,
          target: '_blank'
        },
        {
          href: 'https://jdsn.deere.com/applications',
          label: ALL_OTHER_JDSN_APPLICATIONS,
          target: '_blank'
        }
      ]
    },
    ...(canShowRestrictedNav && isAdmin
      ? [
          {
            component: NavLink,
            to: '/adminFeatures',
            label: 'Admin',
            selected: location.pathname === '/adminFeatures',
            startIcon: <fuel-icon name="settings" />
          }
        ]
      : [])
  ];

  return <Nav items={items} variant="top" />;
}

function AccountMenu({ logout, userInfo }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  return (
    <>
      <Button
        aria-controls={isOpen ? 'account-menu' : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        aria-haspopup="true"
        endIcon={<fuel-icon name="keyboard_arrow_down" />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        variant="headerAction"
      >
        Hi, {userInfo?.given_name}
      </Button>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        onClose={() => setAnchorEl(null)}
        open={isOpen}
      >
        <MenuItem onClick={logout}>Sign out</MenuItem>
      </Menu>
    </>
  );
}

export default function SDFormHeader() {
  const [userInfo, logout] = useUserLogin();
  const [loginState] = useLoginState();
  const roles = loginState.roles;
  const userId = loginState.userId;
  const isAdmin = loginState.isAdmin;
  const hasCreateAccess = loginState.hasCreateAccess;

  return (
    <>
      <Header
        applicationName="Direct Material Strategy Management"
        slotProps={{
          applicationName: { component: NavLink, to: '/' },
          logo: { variant: 'john-deere' }
        }}
      >
        <HeaderActionArea>
          {getEnvVar('VITE_THEME_MODE_SELECTOR_ENABLED') === 'true' && (
            <ThemeModeSelect />
          )}
          <Button
            onClick={(event) =>
              openLink(event, 'https://jdsn.deere.com/applications/DMSM')
            }
            startIcon={<fuel-icon name="help" />}
            variant="headerAction"
          >
            Help
          </Button>
          <Button
            disabled={!userId}
            onClick={(event) => openLink(event, getContactUsLink(userId))}
            startIcon={<fuel-icon name="mail" />}
            variant="headerAction"
          >
            Contact Us
          </Button>
          {loginState.message && (
            <Badge badgeContent={loginState.message} color="warning" />
          )}
          {userInfo ? (
            <AccountMenu logout={logout} userInfo={userInfo} />
          ) : (
            <CircularProgress
              aria-label="Loading user information"
              role="status"
              size={24}
            />
          )}
        </HeaderActionArea>
      </Header>
      <AppNavigation
        hasCreateAccess={hasCreateAccess}
        isAdmin={isAdmin}
        roles={roles}
      />
    </>
  );
}
